import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface InstructorHighlight {
  id: string;
  type: 'total_classes' | 'revenue' | 'most_active_student' | 'mat_utilization' | 'booking_efficiency' | 'total_hours' | 'most_requested_technique' | 'schedule_status' | 'achievement';
  title: string;
  value: string | number;
  description?: string;
  icon?: string;
  // Add more fields as needed
}

export interface InstructorHighlightsData {
  totalClasses: {
    group: number;
    private: number;
    total: number;
  };
  revenue: {
    amount: number;
    currency: string;
  };
  mostActiveStudent: {
    name: string;
    classesCount: number;
    avatarUrl?: string;
    beltRank?: string;
  };
  matUtilization: {
    percentage: number;
    totalSlots: number;
    occupiedSlots: number;
  };
  bookingEfficiency: {
    percentage: number;
    totalBookings: number;
    completedBookings: number;
  };
  totalHours: {
    hours: number;
    minutes: number;
  };
  mostRequestedTechnique: {
    name: string;
    count: number;
  };
  scheduleStatus: {
    completed: number;
    missed: number;
    total: number;
    completedPercentage: number;
  };
  achievements: Array<{
    milestone: number;
    title: string;
    description: string;
    icon: string;
    reached: boolean;
  }>;
}

export const useInstructorHighlights = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: highlightsData, isLoading, error, refetch } = useQuery({
    queryKey: ['instructor-highlights', user?.id],
    queryFn: async (): Promise<InstructorHighlightsData> => {
      if (!user?.id) throw new Error('User not authenticated');

      // Get instructor classes for the current month
      const now = new Date();
      const currentMonth = now.getMonth() + 1; // getMonth() returns 0-11
      const currentYear = now.getFullYear();

      // Define month boundaries for queries
      const startOfMonth = new Date(currentYear, currentMonth - 1, 1);
      const endOfMonth = new Date(currentYear, currentMonth, 0, 23, 59, 59);

      // Use direct query to trainings table (more reliable than RPC)
      
      const { data: classesData, error: classesError } = await supabase
        .from('trainings')
        .select('*')
        .eq('user_id', user.id)
        .gte('start_time', startOfMonth.toISOString())
        .lte('start_time', endOfMonth.toISOString())
        .order('start_time', { ascending: true });

      if (classesError) throw classesError;

      // Early return if no classes data
      if (!classesData || classesData.length === 0) {
        return {
          totalClasses: { group: 0, private: 0, total: 0 },
          revenue: { amount: 0, currency: 'USD' },
          mostActiveStudent: { name: 'No students yet', classesCount: 0 },
          matUtilization: { percentage: 0, totalSlots: 0, occupiedSlots: 0 },
          bookingEfficiency: { percentage: 0, totalBookings: 0, completedBookings: 0 },
          totalHours: { hours: 0, minutes: 0 },
          mostRequestedTechnique: { name: 'No techniques yet', count: 0 },
          scheduleStatus: { completed: 0, missed: 0, total: 0, completedPercentage: 0 },
          achievements: []
        };
      }

      try {
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth();
      const currentYear = currentDate.getFullYear();
      
        // Get current month's start and end dates
        const startOfMonth = new Date(currentYear, currentMonth, 1);
        const endOfMonth = new Date(currentYear, currentMonth + 1, 0, 23, 59, 59);

        // 1. Fetch total classes for current month
        const { data: classesData, error: classesError } = await supabase
          .from('trainings')
          .select('type, status, start_time, end_time')
          .eq('user_id', user.id)
          .gte('start_time', startOfMonth.toISOString())
          .lte('start_time', endOfMonth.toISOString());

        if (classesError) throw classesError;

        // Calculate class counts
        const totalClasses = {
          group: classesData?.filter(c => c.type === 'Group Class').length || 0,
          private: classesData?.filter(c => c.type === 'Private Class').length || 0,
          total: classesData?.length || 0
        };

        // 2. Fetch revenue data (placeholder - implement based on your revenue logic)
        const revenue = {
          amount: 5000.00, // TODO: Implement actual revenue calculation
          currency: 'USD'
        };

        // 3. Fetch most active student
        const { data: studentData, error: studentError } = await supabase
          .from('training_partners')
          .select(`
            profiles!inner(full_name, profile_picture_url, belt_rank),
            trainings!inner(start_time, user_id)
          `)
          .eq('trainings.user_id', user.id)
          .gte('trainings.start_time', startOfMonth.toISOString())
          .lte('trainings.start_time', endOfMonth.toISOString());

        if (studentError) throw studentError;

        // Group by student and count classes
        const studentCounts = new Map();
        studentData?.forEach(partner => {
          const studentName = partner.profiles?.full_name || 'Unknown';
          studentCounts.set(studentName, (studentCounts.get(studentName) || 0) + 1);
        });

        let mostActiveStudent = {
          name: 'No students yet',
          classesCount: 0,
          avatarUrl: undefined,
          beltRank: undefined
        };

        if (studentCounts.size > 0) {
          const [topStudent, count] = Array.from(studentCounts.entries()).reduce((a, b) => a[1] > b[1] ? a : b);
          const topStudentProfile = studentData?.find(p => p.profiles?.full_name === topStudent)?.profiles;
          mostActiveStudent = {
            name: topStudent,
            classesCount: count,
            avatarUrl: topStudentProfile?.profile_picture_url,
            beltRank: topStudentProfile?.belt_rank
          };
        }

        // 4. Calculate Mat Time Utilization
        const completedClasses = classesData?.filter(c => c.status === 'Completed') || [];
        const totalHours = completedClasses.reduce((total, classItem) => {
          if (classItem.start_time && classItem.end_time) {
            const start = new Date(classItem.start_time);
            const end = new Date(classItem.end_time);
            const duration = (end.getTime() - start.getTime()) / (1000 * 60 * 60); // hours
            return total + duration;
          }
          return total;
        }, 0);

        // Assume 8 hours per day, 5 days per week = 40 hours per week = ~160 hours per month
        const totalAvailableHours = 160;
        const matUtilization = {
          percentage: Math.round((totalHours / totalAvailableHours) * 100),
          totalSlots: totalAvailableHours,
          occupiedSlots: Math.round(totalHours)
        };

        // 5. Calculate Booking Efficiency
        const totalBookings = classesData?.length || 0;
        const completedBookings = classesData?.filter(c => c.status === 'Completed').length || 0;
        const bookingEfficiency = {
          percentage: totalBookings > 0 ? Math.round((completedBookings / totalBookings) * 100) : 0,
          totalBookings,
          completedBookings
        };

        // 6. Calculate Total Hours on the Mat
        const totalHoursOnMat = {
          hours: Math.floor(totalHours),
          minutes: Math.round((totalHours % 1) * 60)
        };

        // 7. Fetch Most Requested Technique from training notes
        let mostRequestedTechnique = {
          name: 'No techniques yet',
          count: 0
        };

        // Only fetch notes if there are completed classes AND training IDs are valid
        if (completedClasses.length > 0) {
          const trainingIds = completedClasses.map(c => c.id).filter(Boolean);
          
          // Double check that we have valid training IDs before making the query
          if (trainingIds.length > 0) {
            const { data: notesData, error: notesError } = await supabase
              .from('training_notes')
              .select('content, training_id')
              .in('training_id', trainingIds);

            if (notesData && notesData.length > 0) {
              // Simple technique detection (you might want to implement more sophisticated logic)
              const techniqueKeywords = ['armbar', 'triangle', 'kimura', 'guillotine', 'rear naked choke', 'sweep', 'guard', 'mount', 'side control'];
              const techniqueCounts = new Map();
              
              notesData.forEach(note => {
                techniqueKeywords.forEach(technique => {
                  if (note.content?.toLowerCase().includes(technique.toLowerCase())) {
                    techniqueCounts.set(technique, (techniqueCounts.get(technique) || 0) + 1);
                  }
                });
              });

              if (techniqueCounts.size > 0) {
                const [topTechnique, count] = Array.from(techniqueCounts.entries()).reduce((a, b) => a[1] > b[1] ? a : b);
                mostRequestedTechnique = {
                  name: topTechnique,
                  count
                };
              }
            }
          }
        }

        // 8. Calculate Schedule Status
        const completedClassesCount = classesData?.filter(c => c.status === 'Completed').length || 0;
        const missedClassesCount = classesData?.filter(c => c.status === 'Missed').length || 0;
        const totalScheduledClasses = completedClassesCount + missedClassesCount;
        const scheduleStatus = {
          completed: completedClassesCount,
          missed: missedClassesCount,
          total: totalScheduledClasses,
          completedPercentage: totalScheduledClasses > 0 ? Math.round((completedClassesCount / totalScheduledClasses) * 100) : 0
        };

        // 9. Calculate Achievements (Milestones)
        const totalCompletedClasses = completedClassesCount;
        const milestones = [1, 10, 50, 100, 200, 500, 1000];
        const achievements = milestones.map(milestone => {
          const reached = totalCompletedClasses >= milestone;
          let title = '';
          let description = '';
          let icon = '';

          switch (milestone) {
            case 1:
              title = 'The First Step';
              description = 'On the sOSSiety Mat!';
              icon = '👣🥋';
              break;
            case 10:
              title = 'Ten and Counting';
              description = 'Double-Digit Dedication';
              icon = '🔟💪';
              break;
            case 50:
              title = 'Half a Century of Rolls';
              description = 'Fifty Times Stronger';
              icon = '🗓️💥';
              break;
            case 100:
              title = 'The Century Mark';
              description = '100 Rolls Strong';
              icon = '💯🔥';
              break;
            case 200:
              title = 'The Double Century';
              description = 'A Mat Veteran';
              icon = '🥋⭐';
              break;
            case 500:
              title = 'Half a Thousand Rolls';
              description = 'Road to Mastery';
              icon = '🥇🗺️';
              break;
            case 1000:
              title = 'The Thousand Hour Warrior';
              description = 'Legacy of Discipline';
              icon = '⚔️📜';
              break;
          }

          return {
            milestone,
            title,
            description,
            icon,
            reached
          };
        });

        // Create highlights array
        const highlights: InstructorHighlight[] = [
        {
          id: '1',
          type: 'total_classes',
          title: 'Total Classes Taught',
            value: totalClasses.total,
            description: `${totalClasses.group} Group + ${totalClasses.private} Private`,
          icon: '🥋'
        },
        {
          id: '2',
          type: 'revenue',
            title: 'Revenue',
            value: `$${revenue.amount.toLocaleString()}`,
          description: 'This month',
          icon: '💰'
        },
        {
          id: '3',
            type: 'most_active_student',
            title: 'Most Active Student',
            value: mostActiveStudent.name,
            description: `${mostActiveStudent.classesCount} classes`,
            icon: '👤'
        },
        {
          id: '4',
            type: 'mat_utilization',
            title: 'Mat Time Utilization',
            value: `${matUtilization.percentage}%`,
            description: `${matUtilization.occupiedSlots}/${matUtilization.totalSlots} hours`,
            icon: '⏰'
        },
        {
          id: '5',
            type: 'booking_efficiency',
            title: 'Booking Efficiency',
            value: `${bookingEfficiency.percentage}%`,
            description: `${bookingEfficiency.completedBookings}/${bookingEfficiency.totalBookings} completed`,
            icon: '📊'
          },
          {
            id: '6',
          type: 'total_hours',
          title: 'Total Hours on the Mat',
            value: `${totalHoursOnMat.hours}h ${totalHoursOnMat.minutes}m`,
          description: 'This month',
            icon: '🕐'
          },
          {
            id: '7',
            type: 'most_requested_technique',
            title: 'Most Requested Technique',
            value: mostRequestedTechnique.name,
            description: `${mostRequestedTechnique.count} requests`,
            icon: '🥊'
          },
          {
            id: '8',
            type: 'schedule_status',
            title: 'Your Schedule Status',
            value: `${scheduleStatus.completedPercentage}%`,
            description: `${scheduleStatus.completed}/${scheduleStatus.total} classes`,
            icon: '📅'
          },
          {
            id: '9',
            type: 'achievement',
            title: 'Achievement Unlocked!',
            value: `${achievements.filter(a => a.reached).length} milestones`,
            description: 'Keep pushing!',
            icon: '🏆'
          }
        ];

        return {
          totalClasses,
          revenue,
          mostActiveStudent,
          matUtilization,
          bookingEfficiency,
          totalHours: totalHoursOnMat,
          mostRequestedTechnique,
          scheduleStatus,
          achievements
        };

      } catch (error) {
        console.error('Error fetching instructor highlights:', error);
        throw error;
      }
    },
    enabled: !!user?.id,
  });

  return {
    highlights: highlightsData || null,
    isLoading,
    error,
    refetch,
  };
};



